import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { approvalCategoryApi } from "@/api/approval/approval-category.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiApprovalCategoryPayload } from "@/types/approvals/approval-category.type.ts";
import { Control, FieldValues } from "react-hook-form";

export const approvalCategoriesKeys = createQueryKeys("approvalCategories", {
  all: null,
  byId: (id: number) => [id],
});

export const useApprovalCategories = () => {
  return useQuery({
    ...approvalCategoriesKeys.all,
    queryFn: async () => {
      const result = await approvalCategoryApi.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useApprovalCategoryById = (id: number) => {
  return useQuery({
    ...approvalCategoriesKeys.byId(id),
    queryFn: async () => {
      const result = await approvalCategoryApi.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: !!id,
  });
};

export interface ApprovalCategorySaveParams {
  id: number;
  payload: ApiApprovalCategoryPayload;
}

export const useApprovalCategorySave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id, payload }: ApprovalCategorySaveParams) {
      const result =
        id === 0
          ? await approvalCategoryApi.create(payload)
          : await approvalCategoryApi.update(id, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.all.queryKey,
      });
      if (id !== 0) {
        queryClient.invalidateQueries({
          queryKey: approvalCategoriesKeys.byId(id).queryKey,
        });
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useApprovalCategoryDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (categoryId: number) {
      const { success, error } = await approvalCategoryApi.delete(categoryId);

      if (error || !success) throw new ApiErrorException(error);

      return { categoryId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
